/**
 * A re-import's Word comments made into the records its write stores (decision 58, sync case table C6; part 23c log,
 * tables ID″, DD and AN, principle 3): each comment's author and text cleaned as the app cleans what it stores, its key
 * made from its content (the store makes its ID from the book, its chapter and the key), and its quote anchored in its chapter's text as the write leaves it.
 */
import { chapterVisibleText, ChapterAnchors, commentTime, MAX_AUTHOR, toHex } from '@cockatrice/core';
import type { ReimportedComment, ScanBudget } from '@cockatrice/core';
import { normalizeText } from '@cockatrice/layout';
import type { CommentDraft } from '@cockatrice/storage';

/** Principle 3: a comment's author as a one-line field, its text as free text, each trimmed. */
export interface CleanComment {
  readonly source: ReimportedComment;
  readonly author: string;
  readonly text: string;
}

/** Principle 3 (part 05's table N): `comment`'s author and text cleaned; the reader has cut both to their caps. */
export function cleanComment(comment: ReimportedComment): CleanComment {
  return {
    source: comment,
    author: normalizeText(comment.author, { lines: 'one', max: MAX_AUTHOR }).trim(),
    text: normalizeText(comment.text, { lines: 'many', max: comment.text.length }).trim(),
  };
}

/** ID′ (deep-fix r1): what a key is made from, first naming what it is and its rule's version. */
const KEY_KIND = 'cockatrice-word-comment';

/** A comment ready to anchor: cleaned, with its key (`sourceKey`); the store makes its record ID per chapter (table DD). */
export interface IdentifiedComment extends CleanComment {
  readonly sourceKey: string;
}

/**
 * Tables ID′ (deep-fix r1) and ID″ (deep-fix r2): each comment with its key: the SHA-256 (`digest`) of the cleaned author,
 * the date as written and the cleaned text when Word stamped the comment with a time and no other comment of the file has
 * all three (a new comment gets a new time, so those name one comment across every file of the book); else of those with
 * its raw quote and context and the count of earlier comments alike in all of them, so a key never stands for a comment
 * it can be told apart from. Its record ID is made from the book, its chapter and the key when it is stored (table DD).
 */
export async function identifyComments(comments: readonly CleanComment[], digest: (bytes: Uint8Array) => Promise<Uint8Array>): Promise<IdentifiedComment[]> {
  const encoder = new TextEncoder();
  const content = (comment: CleanComment): string => JSON.stringify([comment.author, comment.source.date, comment.text]);
  const alike = new Map<string, number>();
  for (const comment of comments) alike.set(content(comment), (alike.get(content(comment)) ?? 0) + 1);
  const seen = new Map<string, number>();
  const out: IdentifiedComment[] = [];
  for (const comment of comments) {
    const { author, text, source } = comment;
    let fields: unknown[] = [KEY_KIND, 1, author, source.date, text];
    // ID″2: an untimed comment's author, date and text name no one comment across files.
    if (source.date === null || commentTime(source.date) === null || alike.get(content(comment)) !== 1) {
      const place = JSON.stringify([...fields, source.quote, source.before, source.after]);
      const earlier = seen.get(place) ?? 0;
      seen.set(place, earlier + 1);
      fields = [...fields, source.quote, source.before, source.after, earlier];
    }
    out.push({ ...comment, sourceKey: toHex(await digest(encoder.encode(JSON.stringify(fields)))) });
  }
  return out;
}

/**
 * Table AN: the drafts of `comments` (all of one chapter), anchored in `text`, the chapter's text as the write leaves it,
 * in the order their quotes stand in the file (AN7), within `budget` (AN8).
 */
export function anchorComments(text: string, comments: readonly IdentifiedComment[], budget: ScanBudget): CommentDraft[] {
  const anchors = new ChapterAnchors(chapterVisibleText(text), budget);
  return [...comments]
    .sort((a, b) => a.source.at - b.source.at)
    .map((comment): CommentDraft => {
      const { range, quote, before, after } = anchors.anchor(comment.source);
      return { sourceKey: comment.sourceKey, origin: 'word', author: comment.author, text: comment.text, createdAt: comment.source.date === null ? null : commentTime(comment.source.date), range, quote, before, after };
    });
}
